import React from 'react';
import PropTypes from 'prop-types';

import {LineChart} from '@mui/x-charts';

import {Alert, Box, Paper, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import ChartLegend from '../shared/ChartLegend';
import SectionHeading from '../shared/SectionHeading';
import PanelSkeleton from '../shared/skeletons/PanelSkeleton';

import formatters from '../shared/formatters';

const chartSx = {
  '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
    stroke: '#CEC8BD',
  },
  '& .MuiChartsAxis-tickLabel': {
    fill: '#7B7786',
    fontSize: 11,
  },
  '& .MuiChartsGrid-line': {
    stroke: '#EEE8DD',
    strokeDasharray: '4 4',
  },
};

// Scheduled series use a lighter shade of their confirmed/actual counterpart, and
// the legend labels every series so colour is never the only cue.
const HOURS_SERIES = [
  {key: 'totalHours', color: '#9DB8D2', labelExpression: 'metrics.ClientMetrics.Actuals.ScheduledHours'},
  {key: 'confirmedHours', color: '#2E6DA8', labelExpression: 'metrics.ClientMetrics.Actuals.ConfirmedHours'},
  {key: 'totalPayHours', color: '#B7AFCE', labelExpression: 'metrics.ClientMetrics.Actuals.ScheduledPayHours'},
  {key: 'confirmedPayHours', color: '#6A5D96', labelExpression: 'metrics.ClientMetrics.Actuals.ConfirmedPayHours'},
];

const MONEY_SERIES = [
  {key: 'scheduledRevenue', color: '#8FBF94', labelExpression: 'metrics.ClientMetrics.Actuals.ScheduledRevenue'},
  {key: 'actualRevenue', color: '#2E7D32', labelExpression: 'metrics.ClientMetrics.Actuals.ActualRevenue'},
  {key: 'scheduledLaborCost', color: '#E8B298', labelExpression: 'metrics.ClientMetrics.Actuals.ScheduledLabor'},
  {key: 'actualLaborCost', color: '#DA7955', labelExpression: 'metrics.ClientMetrics.Actuals.ActualLabor'},
  {key: 'grossProfit', color: '#243B63', labelExpression: 'metrics.ClientMetrics.Actuals.GrossProfit'},
];

const ActualsSection = (props) => {
  const {
    isLoading,
    currentSnapshot,
  } = props;

  const {xAxisLabels, hoursSeries, moneySeries, legendItems} = React.useMemo(() => {
    const weeks = currentSnapshot?.actual?.weeks || {};
    const weekKeys = formatters.sortWeekKeysAscending(weeks);
    const buildSeries = (seriesConfigs) => {
      return seriesConfigs.map((seriesConfig) => ({
        id: seriesConfig.key,
        label: translate(seriesConfig.labelExpression),
        color: seriesConfig.color,
        data: weekKeys.map((weekKey) => weeks[weekKey]?.[seriesConfig.key] ?? null),
        showMark: false,
        connectNulls: false,
      }));
    };
    const buildLegend = (seriesConfigs) => {
      return seriesConfigs.map((seriesConfig) => ({
        key: seriesConfig.key,
        label: translate(seriesConfig.labelExpression),
        color: seriesConfig.color,
      }));
    };

    return {
      xAxisLabels: weekKeys.map((weekKey) => formatters.formatWeekLabel(weekKey)),
      hoursSeries: buildSeries(HOURS_SERIES),
      moneySeries: buildSeries(MONEY_SERIES),
      legendItems: {
        hours: buildLegend(HOURS_SERIES),
        money: buildLegend(MONEY_SERIES),
      },
    };
  }, [currentSnapshot]);

  const renderChartPanel = (titleExpression, subtitleExpression, chart, legend, footnoteExpression) => (
    <Paper
      sx={{
        p: 3,
        borderRadius: '12px',
        border: '1px solid #E5E5E5',
        boxShadow: 'none',
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '21px',
          color: '#1A1916',
          mb: 0.25,
        }}
      >
        <Translate expression={titleExpression} />
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          lineHeight: '16px',
          color: '#A8A49E',
          mb: 2,
        }}
      >
        <Translate expression={subtitleExpression} />
      </Typography>
      <Box
        aria-label={translate(titleExpression)}
        sx={{
          border: '1px solid #EBE6DE',
          borderRadius: 3,
          backgroundColor: '#FFFDFC',
          px: 1,
          pt: 1.5,
          pb: 0.5,
        }}
      >
        {chart}
        <ChartLegend items={legend} />
      </Box>
      {
        footnoteExpression &&
        <Typography
          sx={{
            fontSize: '11px',
            lineHeight: '15px',
            color: '#A8A49E',
            mt: 1,
          }}
        >
          <Translate expression={footnoteExpression} />
        </Typography>
      }
    </Paper>
  );

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.Actuals.SectionTitle' />
      {
        isLoading &&
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))'},
            gap: 2,
          }}
        >
          <PanelSkeleton height={300} />
          <PanelSkeleton height={300} />
        </Box>
      }
      {
        !isLoading &&
        !xAxisLabels.length &&
        <Alert severity='info'>
          <Translate expression='metrics.ClientMetrics.EmptyStates.NoActualWeeks' />
        </Alert>
      }
      {
        !isLoading &&
        xAxisLabels.length > 0 &&
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))'},
            gap: 2,
          }}
        >
          {
            renderChartPanel(
                'metrics.ClientMetrics.Actuals.HoursTitle',
                'metrics.ClientMetrics.Actuals.HoursSubtitle',
                <LineChart
                  height={300}
                  margin={{top: 12, right: 16, bottom: 28, left: 60}}
                  grid={{horizontal: true}}
                  slotProps={{legend: {hidden: true}}}
                  xAxis={[{scaleType: 'point', data: xAxisLabels}]}
                  series={hoursSeries}
                  sx={chartSx}
                />,
                legendItems.hours,
                'metrics.ClientMetrics.Actuals.HoursExplainer',
            )
          }
          {
            renderChartPanel(
                'metrics.ClientMetrics.Actuals.MoneyTitle',
                'metrics.ClientMetrics.Actuals.MoneySubtitle',
                <LineChart
                  height={300}
                  margin={{top: 12, right: 16, bottom: 28, left: 60}}
                  grid={{horizontal: true}}
                  slotProps={{legend: {hidden: true}}}
                  xAxis={[{scaleType: 'point', data: xAxisLabels}]}
                  series={moneySeries}
                  sx={chartSx}
                />,
                legendItems.money,
                null,
            )
          }
        </Box>
      }
    </Box>
  );
};

ActualsSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  currentSnapshot: PropTypes.object,
};

export default React.memo(ActualsSection);
